import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Trophy, TrendingUp, TrendingDown, Minus, Shield, Users, Activity, Flame, BadgeCheck } from "lucide-react";
import TopNav from "../components/TopNav";
import FavoritesSheet from "../components/FavoritesSheet";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { fetchRankings } from "../lib/api";
import { useGame } from "../context/GameContext";
import { formatNumber } from "../lib/format";

const RankChange = ({ change }) => {
  if (change === null || change === undefined) {
    return (
      <span className="inline-flex items-center gap-1 font-mono-hud text-xs" style={{ color: "var(--text-dim)" }}>
        <Minus className="w-3 h-3" /> NEW
      </span>
    );
  }
  if (change === 0) {
    return (
      <span className="inline-flex items-center gap-1 font-mono-hud text-xs" style={{ color: "var(--text-dim)" }}>
        <Minus className="w-3 h-3" /> —
      </span>
    );
  }
  const up = change > 0;
  return (
    <span className="inline-flex items-center gap-1 font-mono-hud text-xs font-bold" style={{ color: up ? "var(--highlight)" : "var(--ping-high)" }}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{change}
    </span>
  );
};

const RankingsPage = () => {
  const { gameId: paramGameId } = useParams();
  const navigate = useNavigate();
  const { game, gameId, setGameId } = useGame();
  const [region, setRegion] = useState("all");
  const [favOpen, setFavOpen] = useState(false);
  const activeGameId = paramGameId || gameId;

  useEffect(() => {
    if (paramGameId && paramGameId !== gameId) setGameId(paramGameId);
  }, [paramGameId]); // eslint-disable-line

  const { data, isLoading } = useQuery({
    queryKey: ["rankings", activeGameId, region],
    queryFn: () => fetchRankings(activeGameId, region === "all" ? null : region, 50),
    staleTime: 60_000,
  });

  const items = data?.items || [];
  const regions = useMemo(() => data?.regions || [], [data]);

  useEffect(() => {
    if (data?.game?.name) {
      const scopeLabel = region === "all" ? "Global" : region;
      document.title = `${data.game.name} Top 50 Servers · ${scopeLabel}`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = `Top 50 ${data.game.name} servers ranked by average players × uptime. ${scopeLabel} region. Updated nightly.`;
    }
    return () => { document.title = "Server-Recon · Discover Every Game Server"; };
  }, [data, region]);

  const openServer = (id) => navigate(`/server/${encodeURIComponent(id)}`);
  const niceDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen" data-testid="rankings-page">
      <TopNav onOpenFavorites={() => setFavOpen(true)} />
      <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 hud-caption hover:underline"
          style={{ color: "var(--primary-accent)" }} data-testid="rankings-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO BROWSER
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="hud-caption flex items-center gap-2">
              <Trophy className="w-3 h-3" /> SERVER RANKINGS · UPDATED NIGHTLY
            </div>
            <h1
              className="font-head text-3xl md:text-5xl font-extrabold uppercase mt-1"
              style={{ color: "var(--text-primary)", letterSpacing: "0.02em", lineHeight: 1.1 }}
              data-testid="rankings-title"
            >
              {data?.game?.name || game?.name || "—"} · <span style={{ color: "var(--secondary-accent)" }}>TOP {items.length}</span>
            </h1>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--text-secondary)" }}>
              Score = <span className="font-mono-hud" style={{ color: "var(--primary-accent)" }}>avg_players × uptime%</span>.
              Rolling 7-day window.
            </p>
          </div>
          <Button
            onClick={() => navigate(`/trends/${activeGameId}`)}
            className="font-mono-hud tracking-widest font-bold"
            data-testid="go-to-trends"
            style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}
          >
            <TrendingUp className="w-4 h-4 mr-2" /> VIEW TRENDS
          </Button>
        </div>

        {/* Region tabs */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="region-tabs">
          <div className="hud-caption mr-1">REGION:</div>
          <RegionButton active={region === "all"} onClick={() => setRegion("all")} testid="region-all">
            GLOBAL
          </RegionButton>
          {regions.map((r) => (
            <RegionButton key={r} active={region === r} onClick={() => setRegion(r)} testid={`region-${r}`}>
              {r}
            </RegionButton>
          ))}
        </div>

        {/* Ranking table */}
        <section className="tac-card p-0 overflow-hidden" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border-color)" }}>
            <div>
              <div className="hud-label">LEADERBOARD · {region === "all" ? "GLOBAL" : region}</div>
              <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
                {items.length} RANKED SERVERS
              </div>
            </div>
            <div className="hud-caption">COMPUTED {niceDate(data?.computed_at)}</div>
          </div>

          {isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12" style={{ background: "var(--bg-elevated)" }} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-14 text-center">
              <div className="hud-caption">NO RANKING DATA</div>
              <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
                We need at least a couple of hours of snapshots before this game can be ranked.
                Nightly compute runs at <span className="font-mono-hud" style={{ color: "var(--primary-accent)" }}>02:45 UTC</span>.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="hud-caption text-left" style={{ background: "var(--bg-main)" }}>
                    <th className="px-3 py-2 w-14">#</th>
                    <th className="px-3 py-2 w-14">Δ</th>
                    <th className="px-3 py-2">SERVER</th>
                    <th className="px-3 py-2 hidden md:table-cell">REGION</th>
                    <th className="px-3 py-2 hidden lg:table-cell">MAP</th>
                    <th className="px-3 py-2 text-right">AVG</th>
                    <th className="px-3 py-2 text-right hidden sm:table-cell">PEAK</th>
                    <th className="px-3 py-2 text-right">UPTIME</th>
                    <th className="px-3 py-2 text-right">SCORE</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr
                      key={r.server_id}
                      onClick={() => openServer(r.server_id)}
                      data-testid={`rank-row-${r.rank}`}
                      className="cursor-pointer hover:bg-black/20 transition-colors border-t"
                      style={{ borderColor: "var(--border-color)" }}
                    >
                      <td className="px-3 py-2 font-mono-hud font-bold tabular-nums" style={{ color: r.rank <= 3 ? "var(--secondary-accent)" : "var(--text-primary)" }}>
                        {r.rank <= 3 ? <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3" />{r.rank}</span> : `#${r.rank}`}
                      </td>
                      <td className="px-3 py-2"><RankChange change={r.rank_change} /></td>
                      <td className="px-3 py-2 min-w-[220px] max-w-[380px]">
                        <div className="flex items-center gap-2">
                          {r.official && <Shield className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--official-color, #d4a017)" }} />}
                          {r.verified && <BadgeCheck className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--highlight)" }} />}
                          <span className="truncate font-semibold" style={{ color: "var(--text-primary)" }}>{r.name}</span>
                        </div>
                        <div className="font-mono-hud text-[11px] mt-0.5 truncate" style={{ color: "var(--text-dim)" }}>
                          {r.mode} · {r.map}
                        </div>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell font-mono-hud text-xs" style={{ color: "var(--text-secondary)" }}>{r.region}</td>
                      <td className="px-3 py-2 hidden lg:table-cell truncate max-w-[160px]" style={{ color: "var(--text-secondary)" }}>{r.map}</td>
                      <td className="px-3 py-2 text-right font-mono-hud tabular-nums" style={{ color: "var(--text-primary)" }}>{r.avg_players}</td>
                      <td className="px-3 py-2 text-right font-mono-hud tabular-nums hidden sm:table-cell" style={{ color: "var(--text-secondary)" }}>{r.peak_players}</td>
                      <td className="px-3 py-2 text-right font-mono-hud tabular-nums" style={{ color: r.uptime_pct >= 95 ? "var(--highlight)" : r.uptime_pct >= 80 ? "var(--text-primary)" : "var(--ping-high)" }}>{r.uptime_pct}%</td>
                      <td className="px-3 py-2 text-right font-mono-hud tabular-nums font-bold" style={{ color: "var(--secondary-accent)" }}>{r.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Methodology */}
        <section className="tac-card p-5" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <h2 className="font-head text-xl uppercase font-bold mb-2" style={{ color: "var(--text-primary)" }}>
            How the ranking works
          </h2>
          <ul className="text-sm space-y-1.5 list-disc pl-5" style={{ color: "var(--text-secondary)" }}>
            <li>We score every tracked server using <span className="font-mono-hud" style={{ color: "var(--primary-accent)" }}>avg_players × (uptime% / 100)</span>, so a half-full server that is always online beats a crowded one that is only up half the time.</li>
            <li>Window: last 7 days of minute-level snapshots, rolled up into daily averages at 02:15 UTC.</li>
            <li>Rank changes (Δ column) compare to the previous nightly run.</li>
            <li>Click any row to open the server's full history page.</li>
          </ul>
        </section>

        <FavoritesSheet open={favOpen} onOpenChange={setFavOpen} onOpenServer={(s) => openServer(s.id)} />
      </main>
    </div>
  );
};

const RegionButton = ({ active, onClick, children, testid }) => (
  <Button
    onClick={onClick}
    data-testid={testid}
    variant="outline"
    className="font-mono-hud text-xs tracking-widest"
    style={{
      background: active ? "var(--primary-accent)" : "var(--bg-elevated)",
      color: active ? "#000" : "var(--text-primary)",
      borderColor: active ? "var(--primary-accent)" : "var(--border-strong)",
      borderRadius: 2,
    }}
  >
    {children}
  </Button>
);

export default RankingsPage;
